import React, { useEffect, useState } from 'react';
import 'react-day-picker/dist/style.css';
import { DayPicker } from 'react-day-picker';
import { dateModifiersStyles } from '@teetsh/app/src/components/Form/Dateinput2/dateStyles';
import isBefore from '@teetsh/app/src/utils/time/isBefore';
import isAfter from '@teetsh/app/src/utils/time/isAfter';
import isSameDay from '@teetsh/app/src/utils/time/isSameDay';
import InputTimePicker from '@teetsh/app/src/components/Form/TimePicker2/InputTimePicker';
import tw from 'twin.macro';
import setDateAtAnotherDate from '@teetsh/app/src/utils/time/setDateAtAnotherDate';
import { fr } from 'date-fns/locale/fr';
import { enUS } from 'date-fns/locale/en-US';
import { css } from '@emotion/react';
import getDay from '@teetsh/app/src/utils/time/getDay';
import { UserLocale } from '@teetsh/app/src/types/user';
import { useTranslation } from 'react-i18next';

export type DateRange = [Date | null, Date | null];

export type DateSelectionMode = 'single' | 'range';

interface DatePickerBase {
  dateFormat?: string;
  withTime?: boolean;
  canBeNull?: boolean;
  sundayStart?: boolean;
  calendarRef?: any;
  noMinHeight?: boolean;
  disabledDays?: number[];
  disabledFct?: (d: Date) => boolean;
}

export interface DatePickerSingle extends DatePickerBase {
  mode: 'single';
  value?: Date;
  onChange: (date?: Date) => void;
}

interface DatePickerRange extends DatePickerBase {
  mode: 'range';
  value: DateRange;
  onChange: (date?: DateRange) => void;
}

export type DatePickerProps = {
  fromDate?: Date;
  toDate?: Date;
  mode?: DateSelectionMode;
  dots?: Date[];
  morningDots?: Date[];
  afternoonDots?: Date[];
  userLocale: UserLocale;
  borderless?: boolean;
  fixedWeeks?: boolean;
  onMonthChange?: (date: Date) => void;
} & (DatePickerSingle | DatePickerRange);
const sundayStartFrLocal = {
  ...fr,
  options: { ...fr.options, weekStartsOn: 0 as any },
};
const mondayStartEnLocal = {
  ...enUS,
  options: { ...enUS.options, weekStartsOn: 1 as any },
};

function DayComponent(props: any) {
  return (
    <div tw="relative">
      <div>{props.date.getDate()}</div>
      {props.activeModifiers.dots && (
        <div
          css={[
            tw`absolute -bottom-0.5 -right-1.5 h-1.5 w-1.5 rounded-full bg-brand-intense`,
            props.activeModifiers.selected && tw`bg-white`,
          ]}
        />
      )}
      {props.activeModifiers.morningDots && (
        <div
          css={[
            tw`absolute -bottom-0.5 -right-1 h-2 w-1 rounded-tl-full rounded-bl-full bg-brand-intense`,
            props.activeModifiers.selected && tw`bg-white`,
          ]}
        />
      )}
      {props.activeModifiers.afternoonDots && (
        <div
          css={[
            tw`absolute -bottom-0.5 -right-1 h-2 w-1 rounded-tr-full rounded-br-full bg-brand-intense`,
            props.activeModifiers.selected && tw`bg-white`,
          ]}
        />
      )}
    </div>
  );
}

export default function DatePicker(props: DatePickerProps) {
  const {
    onChange,
    withTime,
    canBeNull,
    noMinHeight,
    calendarRef,
    borderless,
    disabledDays = [],
    disabledFct,
    dots,
    morningDots,
    afternoonDots,
    mode,
    fromDate,
    toDate,
    userLocale,
    fixedWeeks,
    onMonthChange,
  } = props;
  const { t } = useTranslation(['common']);
  const value = canBeNull && props.value === null ? null : props.value;
  const [month, setMonth] = useState<Date>(
    mode === 'range' ? (value as any)[0] : value,
  );
  useEffect(() => {
    setMonth(mode === 'range' ? (value as any)[0] || new Date() : value);
  }, [value]);
  const isBetween = (d: Date) =>
    Array.isArray(value) &&
    value[0] !== null &&
    value[1] !== null &&
    isBefore(d, value[1]) &&
    isAfter(d, value[0]);

  const modifiers: any = {
    start: (d: Date) =>
      Array.isArray(value) && value[0] !== null && isSameDay(d, value[0]),
    end: (d: Date) =>
      Array.isArray(value) && value[1] !== null && isSameDay(d, value[1]),
    // selected: value,
    dots,
    morningDots,
    afternoonDots,
    isBetween,
  };

  function localeToUse() {
    if (userLocale === 'fr-FR' || userLocale === 'fr-BE') {
      if (props.sundayStart) {
        return sundayStartFrLocal;
      }
      return fr;
    }
    if (props.sundayStart) {
      return enUS;
    }
    return mondayStartEnLocal;
  }

  // Le 21.5rem c'est pour éviter que ça saute lorsque veut aller vite pour changer les mois
  return (
    <div tw="flex space-x-8">
      <div
        ref={calendarRef}
        css={[
          !borderless && tw`border rounded-lg bg-white md:p-4`,
          css`
            .rdp-button:hover:not([aria-disabled='true']) {
              ${tw`bg-brand-quiet`}
            }

            .rdp-button:focus,
            .rdp-button:active {
              ${tw`border-2 border-brand-intense`}
            }

            .rdp-day_selected {
              ${tw`bg-brand-intense! font-bold text-white!`}
            }

            .rdp-day_disabled {
              ${tw`text-gray-300! opacity-100!`}
            }

            .rdp-head_cell {
              ${tw`text-muted font-light text-xs`}
            }

            .rdp-caption_label {
              ${tw`text-xl text-neutral-moderate`}
            }

            .rdp-day_today {
              ${tw`text-brand-intense font-extrabold`}
            }
          `,
        ]}
      >
        <DayPicker
          css={css`
            .DayPicker-wrapper {
              ${tw`outline-none`}
              ${!noMinHeight &&
              css`
                min-height: 19rem;
              `}
            }

            .DayPicker-Day--disabled {
              ${tw`text-gray-300! cursor-not-allowed`}
            }
          `}
          locale={localeToUse()}
          modifiers={modifiers}
          disabled={d => {
            if (disabledFct) {
              return disabledFct(d);
            }
            return disabledDays.includes(getDay(d));
          }}
          components={{
            DayContent: DayComponent,
          }}
          modifiersStyles={dateModifiersStyles}
          fromDate={fromDate}
          toDate={toDate}
          month={month}
          onMonthChange={v => {
            setMonth(v);
            if (onMonthChange) {
              onMonthChange(v);
            }
          }}
          mode={mode}
          selected={
            mode === 'range'
              ? { from: (value as any)[0], to: (value as any)[1] }
              : (value as any)
          }
          onSelect={v => {
            if (mode === 'range') {
              return onChange([v.from, v.to]);
            }
            if (mode === 'single') {
              if (v) {
                if (value) {
                  return onChange(setDateAtAnotherDate(value as Date, v));
                }
                return onChange(v);
              }
              return onChange(undefined);
            }
          }}
          fixedWeeks={fixedWeeks}
          showOutsideDays={fixedWeeks} // Meilleur rendu visuel avec les jours des mois voisins
        />
      </div>
      {withTime && mode === 'single' && (
        <div tw="flex justify-center">
          <div tw="w-20 pt-4">
            <InputTimePicker
              id="time"
              label={t('common:components.datePicker.hour')}
              value={value as Date}
              onChange={onChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
